import { useAuth } from '@/hooks/use-auth';

const BONE = 'animate-pulse bg-white/10';

// 10 visible NAV_ITEMS (16 entries, 6 flagged `hidden`) plus the logout row. Hardcoded on purpose:
// importing NAV_ITEMS from dashboard-sidebar would pull ActiveLink, SquadIcon and useAuth into the _app
// chunk, which every page downloads.
const SIDEBAR_ROWS = 10;
const CONTENT_PANELS = [180, 260, 140];

/**
 * Placeholder for the dashboard shell.
 *
 * DashboardLayout wraps the page component, so while its chunk is in flight `dynamic` renders null and
 * the whole viewport — header and sidebar included — is blank. Reproducing the background and the grid
 * costs nothing and removes the white flash.
 *
 * Deliberately imports nothing at runtime beyond the auth hook used by the gate below: this module is a
 * static import of _app, so anything it pulls in ships on every page. In particular it does not reuse the
 * real <Header>, which statically imports SignInModal and would drag yup, react-hook-form and the captcha
 * into the _app chunk.
 *
 * No <h1> and no live region here on purpose: Next's own RouteAnnouncer is an assertive live region that
 * fires on the same route change, and it falls back to the first <h1> in the document.
 */
export function DashboardLayoutSkeleton() {
  return (
    <div aria-hidden='true' data-testid='dashboard-layout-skeleton' className='relative min-h-screen w-full overflow-x-hidden bg-[#080a0c] text-[#f4f6f7]'>
      {/* The two backdrop layers of dashboard-layout.tsx. Pure CSS, and this is what kills the white screen. */}
      <div className='pointer-events-none fixed inset-0 bg-[radial-gradient(50%_30%_at_50%_0%,rgba(156,255,46,0.12),transparent_70%),radial-gradient(32%_22%_at_82%_14%,rgba(46,230,255,0.08),transparent_72%),linear-gradient(180deg,#080a0c_0%,#0a0c0e_44%,#050607_100%)]' />
      <div className='pointer-events-none fixed inset-0 opacity-[0.24] [background-image:linear-gradient(rgba(255,255,255,0.045)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.035)_1px,transparent_1px)] [background-size:56px_56px] [mask-image:linear-gradient(180deg,#000_0%,transparent_72%)]' />
      {/* Header chrome remarked by hand. py-5 because the real header's `isScrolled` starts false. */}
      <div className='sticky top-0 z-50 flex w-full items-center justify-center border-b border-white/10 bg-[linear-gradient(180deg,rgba(6,8,10,0.92),rgba(6,8,10,0.70))] backdrop-blur-[14px]'>
        <div className='mx-auto flex w-full max-w-[1400px] items-center justify-between gap-5 p-5'>
          <div className={`${BONE} h-[22px] w-[100px] rounded lg:h-10 lg:w-[180px]`} />
          <div className='flex items-center gap-4 lg:gap-8'>
            <div className={`${BONE} h-5 w-16 rounded`} />
            <div className={`${BONE} h-9 w-28 rounded-[10px]`} />
          </div>
        </div>
      </div>
      {/* 286px because `collapsed` is read from localStorage in an effect: the real layout's first frame is
          always the expanded one, whatever the user's stored preference. */}
      <div className='relative z-[1] mx-auto grid w-full max-w-[1400px] gap-0 px-5 py-6 lg:grid-cols-[286px_minmax(0,1fr)]'>
        <div className='mb-4 min-w-0 lg:sticky lg:top-6 lg:mb-0'>
          <div className='flex min-w-0 flex-col rounded-xl border border-white/10 bg-[#0d1114]/[0.88] p-2.5 shadow-[0_30px_90px_-60px_rgba(0,0,0,0.95)] backdrop-blur-xl'>
            <div className='rounded-lg border border-[rgba(156,255,46,0.22)] bg-[linear-gradient(135deg,rgba(156,255,46,0.10),rgba(255,255,255,0.035)_48%,rgba(46,230,255,0.07))] p-4 max-sm:p-3'>
              <div className='flex items-center gap-3'>
                {/* Solid green, not a bone: this is exactly what the real sidebar paints while `user` is
                    still null, so the swap is pixel-identical — and it puts the brand colour on screen. */}
                <div className='size-11 shrink-0 rounded-lg bg-[#9cff2e]' />
                <div className='flex min-w-0 flex-col gap-1.5'>
                  <span className={`${BONE} h-4 w-24 rounded`} />
                  <span className={`${BONE} h-3 w-32 rounded`} />
                </div>
              </div>
            </div>
            <div className='mt-4 grid gap-2.5'>
              {Array.from({ length: SIDEBAR_ROWS }, (_unused, rowIndex) => (
                <div key={rowIndex} className={`${BONE} h-10 w-full rounded-lg`} />
              ))}
              <div className={`${BONE} mt-2 h-10 w-full rounded-lg`} />
            </div>
          </div>
        </div>
        <div className='flex min-w-0 flex-col gap-4 lg:ps-6'>
          {CONTENT_PANELS.map((panelHeight) => (
            <div key={panelHeight} className='rounded-lg border border-white/10 bg-white/[0.035] p-4' style={{ minHeight: panelHeight }}>
              <div className={`${BONE} h-4 w-40 rounded`} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * The skeleton behind a session gate, shared by the two places that wait on the dashboard.
 *
 * Only draws for someone whose session was already restored from localStorage by AuthContext (no network
 * involved). Without the gate, an anonymous visitor typing /dashboard/overview would see a complete fake
 * dashboard during the `me()` round trip and only then be redirected to `/` — a placeholder promising a
 * destination that is not going to happen. With no local session it keeps today's blank.
 */
export function DashboardLayoutFallback() {
  const { user } = useAuth();

  return user ? <DashboardLayoutSkeleton /> : null;
}
